import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="promotional-code"
// Validates a promotional code live against the public endpoint and blocks form
// submission when a code was typed but not validated.
export default class extends Controller {
  static targets = ["input", "button", "message"]
  static values = {
    url: String,
    success: String,
    invalid: String,
    notValidated: String
  }

  connect() {
    this.validated = false
    this.validatedFor = null
  }

  async validateCode() {
    const code = this.inputTarget.value.trim()

    if (code === "") {
      this.clearMessage()
      this.setInputState(null)
      this.validated = false
      this.validatedFor = null
      return
    }

    try {
      const response = await fetch(`${this.urlValue}?code=${encodeURIComponent(code)}`, {
        headers: { Accept: "application/json" }
      })
      const data = await response.json()

      if (data.valid) {
        this.validated = true
        this.validatedFor = this.inputTarget.value
        this.showMessage(this.successValue, "text-success")
        this.setInputState("is-valid")
      } else {
        this.validated = false
        this.validatedFor = null
        this.showMessage(this.invalidValue, "text-danger")
        this.setInputState("is-invalid")
      }
    } catch {
      this.validated = false
      this.validatedFor = null
      this.showMessage(this.invalidValue, "text-danger")
      this.setInputState("is-invalid")
    }
  }

  // Editing the code after a successful validation invalidates it: forces a re-validation.
  reset() {
    if (this.inputTarget.value !== this.validatedFor) {
      this.validated = false
      this.clearMessage()
      this.setInputState(null)
    }
  }

  checkSubmit(event) {
    // A form-reload round-trip inserts a hidden `reload` input; never block those.
    if (this.element.querySelector('input[name="reload"]')) return

    if (this.inputTarget.value.trim() !== "" && !this.validated) {
      event.preventDefault()
      this.showMessage(this.notValidatedValue, "text-danger")
      this.setInputState("is-invalid")
      this.inputTarget.focus()
    }
  }

  showMessage(text, className) {
    this.messageTarget.textContent = text
    this.messageTarget.className = `mt-1 ${className}`
  }

  clearMessage() {
    this.messageTarget.textContent = ""
    this.messageTarget.className = "mt-1"
  }

  // Toggle Bootstrap's validation border on the input to match the current state.
  // Pass null to clear both classes.
  setInputState(className) {
    this.inputTarget.classList.remove("is-valid", "is-invalid")
    if (className) this.inputTarget.classList.add(className)
  }
}
